import { useCallback, useMemo } from 'react';
import {
	buildScopedLiquidExpression,
	parseScopedLiquidExpression,
} from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/bindPanel.utils.ts';
import type { FieldBindingRes } from '@dealstack/frontend/shared/components/FieldBinding/useFieldBindingState.types.ts';
import type {
	FieldBindingCatalogueRes,
	SecurityScopeResult,
} from '@dealstack/frontend/shared/components/FieldBinding/useSecurityScope.types.ts';

/**
 * The security extras a programme-scoped template offers on top of the shared binding state.
 * Shared by Master Documents and KYC forms; the global library has no programme and supplies none.
 */
export const useSecurityScope = ({
	catalogue,
	bindings,
	onChangeBinding,
}: {
	catalogue: FieldBindingCatalogueRes | undefined;
	bindings: FieldBindingRes[];
	onChangeBinding: (fieldKey: string, bindingTemplate: string) => void;
}): SecurityScopeResult => {
	const securities = useMemo(() => catalogue?.securities ?? [], [catalogue]);
	const scopedSamples = useMemo(() => catalogue?.scopedSamples ?? {}, [catalogue]);

	// A document almost always speaks about one security throughout, so repointing each field by
	// hand is the tedium this exists to remove. Only already-scoped bindings are touched: a
	// scalar or list binding has no security to repoint, and rewriting one would silently change
	// its meaning.
	const onApplySecurityToAll = useCallback(
		(securityId: string) => {
			bindings.forEach((row) => {
				const parsed = parseScopedLiquidExpression(row.bindingTemplate);
				if (!parsed || parsed.securityId === securityId) {
					return;
				}
				onChangeBinding(
					row.fieldKey,
					buildScopedLiquidExpression({ ...parsed, securityId })
				);
			});
		},
		[bindings, onChangeBinding]
	);

	return { securities, scopedSamples, onApplySecurityToAll };
};
